'use client';

import { useState, useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function TransformationSection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const updateSliderPosition = (clientX: number) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      updateSliderPosition(e.clientX);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length > 0) {
      updateSliderPosition(e.touches[0].clientX);
    }
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  return (
    <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-black via-card to-black">
      <div className="max-w-[1400px] mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="text-primary font-heading text-sm tracking-[0.3em] mb-4 block">
            RESULTADOS COMPROBADOS
          </span>
          <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl text-white mb-6 tracking-tight">
            TRANSFORMACIÓN
            <br />
            <span className="text-gradient-red">GARANTIZADA</span>
          </h2>
          <p className="text-white/60 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Desliza para ver la perfección de nuestro detallado
          </p>
        </div>

        {/* Before/After Comparison */}
        <div 
          ref={containerRef}
          className="relative max-w-5xl mx-auto rounded-[32px] overflow-hidden border border-white/10 shadow-2xl select-none"
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchMove={handleTouchMove}
          onTouchStart={() => setIsDragging(true)}
          onTouchEnd={() => setIsDragging(false)}
        >
          {/* Container with aspect ratio */}
          <div className="relative w-full aspect-video">
            {/* BEFORE Image - Damaged/Dull Paint (Base Layer - Always Visible) */}
            <div className="absolute inset-0">
              <AppImage
                src="/assets/images/porsche_911_ceramic_coated_after.png"
                alt="Porsche 911 negro con acabado espejo perfecto, brillo profundo, reflejos nítidos de luces LED y negro intenso - resultado del recubrimiento cerámico profesional"
                className="w-full h-full object-cover"
              />
            </div>

            {/* AFTER Image - Perfect Mirror Finish (Top Layer - Clipped by slider) */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{
                width: `${sliderPosition}%`
              }}
            >
              <AppImage
                src="/assets/images/porsche_911_damaged_paint_before.png"
                alt="Porsche 911 negro con pintura dañada, rayones circulares, swirls visibles y opacidad por el sol - estado antes del tratamiento"
                className="w-full h-full object-cover"
                style={{
                  width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%',
                  maxWidth: 'none'
                }}
              />
            </div>
          </div>

          {/* Slider Handle - Red (#E60000) */}
          <div
            className="absolute top-0 bottom-0 w-1 bg-[#E60000] z-10 pointer-events-none"
            style={{ left: `${sliderPosition}%` }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-[#E60000] rounded-full border-4 border-white shadow-2xl flex items-center justify-center cursor-ew-resize hover:scale-110 transition-transform duration-200 pointer-events-auto">
              <div className="flex gap-1">
                <div className="w-0.5 h-4 bg-white rounded-full" />
                <div className="w-0.5 h-4 bg-white rounded-full" />
              </div>
            </div>
          </div>

          {/* Labels - ANTES and DESPUÉS */}
          <div className="absolute top-8 left-8 bg-black/70 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 pointer-events-none">
            <span className="text-white text-sm md:text-base font-heading tracking-[0.2em] font-bold">
              ANTES
            </span>
          </div>
          <div className="absolute top-8 right-8 bg-[#E60000]/90 backdrop-blur-md px-6 py-3 rounded-full border border-white/20 shadow-lg pointer-events-none">
            <span className="text-white text-sm md:text-base font-heading tracking-[0.2em] font-bold">
              DESPUÉS
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 max-w-5xl mx-auto">
          {[
            { id: 'stat_1', value: '10+', label: 'Años de Garantía' },
            { id: 'stat_2', value: '99%', label: 'Protección UV' },
            { id: 'stat_3', value: '500+', label: 'Vehículos Protegidos' },
            { id: 'stat_4', value: '100%', label: 'Satisfacción Garantizada' }
          ].map((stat) => (
            <div key={stat.id} className="text-center">
              <div className="text-4xl md:text-5xl font-heading text-primary mb-2">
                {stat.value}
              </div>
              <div className="text-white/60 text-sm tracking-wider">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}